import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Box, List, Grid, Button, ListItem, Typography, ListItemText } from '@material-ui/core';

import { useStyles, listTextStyles, customCardStyles, collapseCardStyles } from './Details.styled';
import { useConfirm } from 'hooks';
import { DocumentsActions } from 'state/modules/documents';
import { userGroupsActions } from 'state/modules/userGroups';
import { UpdateDealerModal } from 'components/Modals';
import { useHistory, withRouter } from 'react-router-dom';
import { Card } from 'components/UI';
import Can from 'components/Can';
import i18n from 'i18n-js';
import { CollapseCard } from 'components/UI';
import { buildUrl } from 'utils';

const Details = ({ id }) => {
  const classes = useStyles();
  const history = useHistory();
  const dispatch = useDispatch();
  const listClasses = listTextStyles();
  const cardClasses = customCardStyles();
  const [open, setOpen] = useState(false);
  const { file } = useSelector((state) => state.documents);
  const collapseCardClasses = collapseCardStyles();
  const { userGroup, fetchedDeleteUserGroupSuccess } = useSelector((state) => state.userGroups);

  const download = () => {
    const zipURL = window.URL.createObjectURL(file);
    const tempLink = document.createElement('a');
    tempLink.href = zipURL;
    tempLink.download = `documents-${Math.floor(Date.now() / 1000)}.zip`;
    tempLink.click();
    dispatch(DocumentsActions.resetFile());
  };

  const [toggleDeleteUserGroup, DeleteUserGroupConfirm] = useConfirm({
    title: i18n.t('user_groups.delete_user_group'),
    message: i18n.t('user_groups.delete_user_group_confirm'),
    cancelText: i18n.t('common.cancel'),
    confirmText: i18n.t('common.delete'),
    onConfirm: () => {
      dispatch(userGroupsActions.deleteUserGroup(id));
    },
  });

  useEffect(() => {
    if (fetchedDeleteUserGroupSuccess) {
      dispatch(userGroupsActions.resetDeleteUserGroup());
      history.push(buildUrl(`/dealers`));
    }
  }, [fetchedDeleteUserGroupSuccess, dispatch, history]);

  useEffect(() => {
    if (id) {
      dispatch(userGroupsActions.fetchUserGroupById(id, 'details', 'business'));
    }
  }, []) // eslint-disable-line

  useEffect(() => {
    if (file) {
      download();
    }
  }, [file]) // eslint-disable-line

  /**
   * Parse dealer data
   * @param {object} user Object with dealer data
   */
  const getDealerData = (userGroup = {}) => {
    const ug = (userGroup?.default_vehicle_group || []).map((val) => {
      return {
        label: val?.name,
        value: '',
        id: val?.id,
      };
    });
    const dealerInfo = [
      {
        title: i18n.t('common.basic_info'),
        items: [
          { label: i18n.t('user_groups.name'), value: `${userGroup?.name || ''}` || '-' },
          { label: i18n.t('common.phone'), value: userGroup?.phone_number || '-' },
          { label: i18n.t('common.address'), value: userGroup?.address || '-' },
          { label: i18n.t('common.state'), value: userGroup?.state || '-' },
          { label: i18n.t('common.city'), value: userGroup?.city || '-' },
          { label: i18n.t('common.zip_code'), value: userGroup?.zip_code || '-' },
        ],
      },
      {
        title: i18n.t('user_groups.vehicle_groups'),
        items: ug,
      },
    ];

    return dealerInfo;
  };

  const handleOpenUpdateModal = () => {
    setOpen((prevState) => !prevState);
  };

  /**
   * handle Delete user group modal
   */
  const handleDeleteGroupModal = () => {
    toggleDeleteUserGroup();
  };

  const items = [
    {
      title: i18n.t('user_groups.delete_user_group'),
      onClick: handleDeleteGroupModal,
    },
  ];

  return (
    <Card
      menuItems={items}
      menuPermissions={{
        action: 'update',
        subject: 'user',
        type: 'can',
      }}
      title={i18n.t('user_groups.dealer')}
      headerActionType="vertical"
      contentRootClasses={cardClasses}
    >
      <Box className={classes.cardContentPadding}>
        <Grid
          container
          direction="row"
          alignItems="center"
          className={classes.gridContainer}
          spacing={1}
        >
          <Grid item xs={8}>
            <Typography variant="h3">
              <Box fontSize={16} color="#2C2C2E" fontWeight="500">
                ID# {id}
              </Box>
            </Typography>
          </Grid>
          <Can I="update" on="user_group">
            <Grid item xs={4} className={`${classes.textRight}`}>
              <Typography>
                <Button
                  color="primary"
                  onClick={handleOpenUpdateModal}
                  className={classes.userGroupLink}
                >
                  {i18n.t('vehicle.update_info')}
                </Button>
              </Typography>
            </Grid>
          </Can>
        </Grid>

        <div>
          {getDealerData(userGroup).map((val, index) => (
            <CollapseCard key={index} title={val.title} customClasses={collapseCardClasses}>
              <List className={classes.userList}>
                {val.items.map((val, index) => (
                  <ListItem key={index} className={classes.userListItem}>
                    <ListItemText
                      classes={listClasses}
                      primary={val.label}
                      onClick={() => {
                        if (val?.id) {
                          if (orgType !== 'msOwnerOrg') {
                            history.push(buildUrl(`/service-groups/${val.id}`));
                          } else {
                            history.push(buildUrl(`/fleets/${val.id}`));
                          }
                        }
                      }}
                      secondary={
                        <Box
                          display="flex"
                          component="span"
                          flexDirection="column"
                          className={classes.textRight}
                        >
                          <Typography
                            variant="body2"
                            component="span"
                            color="textSecondary"
                            className={classes?.[val.classes]}
                          >
                            {val.value}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </CollapseCard>
          ))}
        </div>
        <DeleteUserGroupConfirm />
        {open && (
          <UpdateDealerModal
            id={id}
            open={open}
            userGroup={userGroup}
            handleClick={handleOpenUpdateModal}
          />
        )}
      </Box>
    </Card>
  );
};

export default withRouter(Details);
